import { useEffect, useState } from 'react';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useSelector } from 'react-redux';
import { Alert } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation, Trans } from 'react-i18next';
import { solutionsArticleUrl } from '../config/solutionsArticles';
import API from '../api/api';

const CCEnablePlaceholderMatchingDialog = ({ onUpdateThemeSettings, showWhenPlaceholderDetected, marginTop }) => {
  const { t } = useTranslation();
  const themes = useSelector(state => state.themes);
  const editTheme = useSelector(state => state.themes.edit_theme);
  const shopUrl = useSelector(state => state.shop.shop.url);
  const [shouldRender, setShouldRender] = useState(false);
  const [cachedOptions, setCachedOptions] = useState(null);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    // Only run once when component mounts
    if (settingsLoaded) return;

    // Check if placeholder matching is already enabled in localStorage
    const localStorageKey = `ly-cc-placeholder-matching-enabled-${shopUrl}`;
    const isEnabled = localStorage.getItem(localStorageKey) === 'true';
    
    if (isEnabled) {
      setShouldRender(false);
      setSettingsLoaded(true);
      return;
    }

    // Load theme settings to check current state
    const theme = themes.themes.find(x => x.id == editTheme);
    if (theme) {
      setCurrentTheme(theme);
      API.getMetafield('global', `ly_theme_settings_${theme.shopify_id}`, (data) => {
        if (data.success && data.result && data.result.value) {
          let options = JSON.parse(data.result.value);
          if (options) {
            // Cache the options for later use
            setCachedOptions(options);
            // Check if usePlaceholderMatching is already enabled
            if (options.usePlaceholderMatching) {
              localStorage.setItem(localStorageKey, 'true');
              setShouldRender(false);
            } else {
              setShouldRender(true);
            }
          } else {
            // Create default options if none exist
            const defaultOptions = {
              debug: false,
              lazyload: false,
              observe: true,
              observeLinks: false,
              observeImages: true,
              observeCustomContents: true,
              linksBlacklist: [],
              loadJquery: false,
              nonStrict: false,
              languageInCartAttribute: false,
              usePlaceholderMatching: false
            };
            setCachedOptions(defaultOptions);
            setShouldRender(true);
          }
        } else {
          // Create default options if API call failed
          const defaultOptions = {
            debug: false,
            lazyload: false,
            observe: true,
            observeLinks: false,
            observeImages: true,
            observeCustomContents: true,
            linksBlacklist: [],
            loadJquery: false,
            nonStrict: false,
            languageInCartAttribute: false,
            usePlaceholderMatching: false
          };
          setCachedOptions(defaultOptions);
          setShouldRender(true);
        }
        setSettingsLoaded(true);
      });
    }
  }, []); // Empty dependency array - only run once on mount

  // Watch for editTheme changes and clear localStorage flag
  useEffect(() => {
    if (settingsLoaded) {
      // Clear localStorage flag when theme changes so dialog can appear again for new theme
      const localStorageKey = `ly-cc-placeholder-matching-enabled-${shopUrl}`;
      localStorage.removeItem(localStorageKey);
      // Reset component state to check new theme
      setShouldRender(false);
      setCachedOptions(null);
      setCurrentTheme(null);
      setSettingsLoaded(false);
    }
  }, [editTheme]);

  const handleUpdateClick = () => {
    if (cachedOptions && currentTheme) {
      // Update the cached options to enable placeholder matching
      const updatedOptions = {
        ...cachedOptions,
        usePlaceholderMatching: true
      };

      // Save the updated options back to the API
      API.saveCustomMetafield('global', `ly_theme_settings_${currentTheme.shopify_id}`, JSON.stringify(updatedOptions), 'json_string', (res) => {
        if (res.success) {
          // Set localStorage flag to prevent showing dialog again
          const localStorageKey = `ly-cc-placeholder-matching-enabled-${shopUrl}`;
          localStorage.setItem(localStorageKey, 'true');
          // Hide the dialog
          setShouldRender(false);
          // Call the callback if provided
          if (onUpdateThemeSettings) {
            onUpdateThemeSettings();
          }
        }
      });
    }
  };

  // Only show if placeholder detected AND settings indicate it should be shown
  if (!shouldRender || !showWhenPlaceholderDetected) {
    return null;
  }

  return (
    <Alert severity="error" sx={(theme) => ({
        mb: theme.spacing(2),
        // sx treats marginTop as a spacing key, so a bare number would be multiplied by 8
        marginTop: marginTop ? `${marginTop}px` : 0,
      })}
      action={
        <Tooltip title={t('navigation.secondary.documentation')}>
          <IconButton size="small" color="inherit" component="a" href={solutionsArticleUrl('customContentPlaceholderMatching')} target="_blank" rel="noopener noreferrer">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Typography variant="body2">
        <Trans i18nKey="placeholderMatchingDialog.description" />
      </Typography>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={handleUpdateClick}
        sx={(theme) => ({ mt: theme.spacing(1) })}
      >
        {t('placeholderMatchingDialog.updateButton')}
      </Button>
    </Alert>
  );
};

export default CCEnablePlaceholderMatchingDialog;